import { Injectable } from "@angular/core";
import { METRIC } from "@backend/common/analytics/metrics";
import Bugsnag from "@bugsnag/js";
import { USER_SESSION } from "@shared/constants";
import { Subscription, lastValueFrom, Observable, interval, debounceTime, skipWhile, tap, switchMap, throwError, of } from "rxjs";
import { BaseSessionService } from "@apps/patient-core/src/app/services/abstracts/session.service";
import { FeatureFlagsService } from "@apps/patient-core/src/app/services/feature-flags.service";
import { AnalyticsService } from "@apps/patient-core/src/app/services/analytics.service";
import { CacheService } from "@apps/patient-core/src/app/services/cache.service";
import { GAService } from "@apps/patient-core/src/app/services/ga.service";
import { HttpService } from "@apps/patient-core/src/app/services/http.service";
import { BaseAuthService } from "@apps/patient-core/src/app/services/abstracts/auth.service";
import { LocationService } from "@apps/patient-core/src/app/services/location.service";
import { NavigationService } from "@apps/patient-core/src/app/services/navigation.service";
import { WindowService } from "@apps/patient-core/src/app/services/window.service";
import { ActivityMonitorService } from "@apps/patient-core/src/app/services/activity-monitor.service";
import { Constants } from "@apps/patient-app/constants";

const { SESSION_EXPIRY, KEEP_ALIVE_INTERVAL } = USER_SESSION.PATIENT;
const LAST_ACTIVITY_KEY = "lastActivity";
const SESSIONS_PATH = "/api/v1/sessions";

interface I_CachedActivity {
  timestamp: number;
}

@Injectable({
  providedIn: "root",
})
export class PatientSessionService extends BaseSessionService {
  private _intervalSubscription: Subscription;
  private _activitySubscription: Subscription;
  private _focusSubscription: Subscription;
  private _invalidJWTSubscription: Subscription;
  private _lastActivity: number;

  constructor(
    protected _httpService: HttpService,
    protected _authService: BaseAuthService,
    protected _cacheService: CacheService,
    protected _gaService: GAService,
    protected _windowService: WindowService,
    protected _navigationService: NavigationService,
    protected _locationService: LocationService,
    protected _analyticsService: AnalyticsService,
    protected _featureFlagsService: FeatureFlagsService,
    private _activityMonitorService: ActivityMonitorService
  ) {
    super(_httpService, _authService, _cacheService, _gaService, _windowService, _navigationService, _locationService, _analyticsService, _featureFlagsService);
  }
  public get isPasswordRequired(): boolean {
    if (!this._authService.isLoggedIn()) {
      // These should not need clearing but just in case
      this._clearLastActivity();
      this._clearActivitySubscriptions();

      return false;
    }

    return this._isPasswordRequired(this._lastActivity);
  }

  public onPageUnload(): void {}

  private _restoreLastActivity(): void {
    const activityCache = this._cacheService.getJson<I_CachedActivity>(LAST_ACTIVITY_KEY);

    if (activityCache) {
      this._lastActivity = activityCache.timestamp;
    }
  }

  public async init(): Promise<void> {
    this._lastActivityInit();

    try {
      const jwt = await lastValueFrom(this._getSession());

      if (!jwt) return;

      const existingJWT = this._authService.getJWTString();

      if (!!existingJWT) {
        if (existingJWT !== jwt) Bugsnag.notify(new Error("JWT stored in local storage does not match the one returned from the server"));

        return;
      }

      // TODO: once we're happy that this is working (i.e. no Bugsnag errors are being reported) we can set the token in memory and
      //       prevent it being stored the local storage
    } catch (error) {
      Bugsnag.notify(error);
    }
  }

  private _lastActivityInit(): void {
    this._restoreLastActivity();

    if (this._authService.isLoggedIn()) {
      if (!this._lastActivity) {
        this._lastActivity = this._activityMonitorService.lastActivity;
      }

      if (this.isPasswordRequired) {
        this._navigateToReEnterPassword();
      }
    }
  }

  private _setSession(): Observable<string | any[] | null> {
    return this._httpService.send(SESSIONS_PATH, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${this._authService.getJWTString()}`,
      },
    });
  }

  protected _setSessionId(): void {
    try {
      this._setSession().subscribe();
    } catch (error) {
      Bugsnag.notify(error);
    }
  }

  public clear(): Promise<void> {
    this._clearLastActivity();

    return super.clear();
  }

  public startKeepAlive(): void {
    if (!this._authService.isLoggedIn()) {
      this._cacheService.delete(LAST_ACTIVITY_KEY);

      // We only want to keep the session alive for logged in patients because other mechanisms are in place for other access levels
      return;
    }

    this._clearActivitySubscriptions();
    this._restoreLastActivity();

    if (this._isPasswordRequired(this._lastActivity)) {
      this._navigateToReEnterPassword();

      return;
    }

    this._intervalSubscription = interval(KEEP_ALIVE_INTERVAL).subscribe(() => {
      if (Date.now() - this._lastActivity > KEEP_ALIVE_INTERVAL) {
        // No activity since last keep alive
        if (this._isPasswordRequired(this._lastActivity)) {
          this._navigateToReEnterPassword();
        }

        return;
      }

      this._handleActivity();
    });

    this._activitySubscription = this._activityMonitorService.activity.pipe(debounceTime(1000)).subscribe((activity) => {
      if (this._isPasswordRequired(this._lastActivity)) {
        this._navigateToReEnterPassword();

        return;
      }

      this._lastActivity = activity.timestamp;
      this._cacheLastActivity();
    });

    this._focusSubscription = this._activityMonitorService.focus
      .pipe(skipWhile((focus) => focus)) // Ignore the default value
      .subscribe(() => {
        if (this._isPasswordRequired(this._lastActivity)) {
          this._navigateToReEnterPassword();
        } else {
          this._lastActivity = Date.now();
        }

        this._handleActivity();
      });

    this._invalidJWTSubscription = this._authService.onInvalidJWT.subscribe(() => {
      this._clearLastActivity();
    });
  }

  private _clearLastActivity(): void {
    this._cacheService.delete(LAST_ACTIVITY_KEY);
    this._activityMonitorService.clear();
  }

  private _cacheLastActivity(): void {
    const jwt = this._authService.getJWT();

    if (!jwt) {
      return;
    }

    this._cacheService.setJson<I_CachedActivity>(
      LAST_ACTIVITY_KEY,
      {
        timestamp: this._lastActivity,
      },
      this._authService.getJWT().exp * 1000
    );
  }

  public onPasswordValidated(): void {
    this._cacheService.delete(LAST_ACTIVITY_KEY);
    this._lastActivity = Date.now();
    this._cacheLastActivity();
    this._locationService.href = "/";
  }

  public extendSession(password: string): Observable<void> {
    Bugsnag.leaveBreadcrumb("extending session with password");

    return this._extendSession(Date.now(), password).pipe(
      tap({
        next: () => {
          this._lastActivity = Date.now();
          this.startKeepAlive();
        },
        error: (error) => {
          Bugsnag.notify(error);
        },
      })
    );
  }

  private _clearActivitySubscriptions(): void {
    const subscriptions = ["_intervalSubscription", "_activitySubscription", "_focusSubscription", "_invalidJWTSubscription"];

    subscriptions.forEach((subscription) => {
      if (subscription in this && this[subscription] instanceof Subscription) {
        this[subscription].unsubscribe();
      }
    });
  }

  private _handleActivity(): void {
    if (this._isPasswordRequired(this._lastActivity)) {
      this._navigateToReEnterPassword();

      return;
    }

    this._lastActivity = Date.now();

    this._extendSession(this._lastActivity).subscribe({
      error: () => {
        this._navigateToReEnterPassword();
      },
    });
  }

  private _extendSession(lastActivity: number, password?: string): Observable<void> {
    let args = `last_activity:${(lastActivity / 1000).toFixed(0)}`;

    if (password) {
      args += `, password:"${password}"`;
    }

    return this._httpService.mutation("extendSession", `{ extendSession(${args}) }`).pipe(
      switchMap((response) => {
        if (response.errors?.length) {
          return throwError(() => new Error(response.errors[0].message));
        }

        return of(response);
      })
    );
  }

  private _isPasswordRequired(lastActivity: number): boolean {
    return Date.now() - lastActivity > SESSION_EXPIRY - KEEP_ALIVE_INTERVAL;
  }

  private _navigateToReEnterPassword(): void {
    this._clearActivitySubscriptions();

    this._navigationService.navigate(Constants.ROUTES.LOGIN_RE_ENTER_PASSWORD.path);

    this._analyticsService.track(new METRIC.SessionExpired(this._lastActivity));
  }
}
